import * as React from "react"

import { Hash, Link2 } from "lucide-react"

import { Loader } from "../Loader/Loader"

export interface ResourceMenuAction {
  label: string
  onSelect: () => void
  icon?: React.ReactNode
  destructive?: boolean
  disabled?: boolean
  loading?: boolean
  "data-testid"?: string
}

export interface ResourceMenuCopyTargets {
  id?: string
  url?: string
}

type ResourceMenuItemComponent = React.ComponentType<{
  children: React.ReactNode
  className?: string
  destructive?: boolean
  disabled?: boolean
  onSelect?: (event: Event) => void
  "data-testid"?: string
}>

export interface ResourceMenuItemsProps {
  actions: ResourceMenuAction[]
  copy: ResourceMenuCopyTargets
  onCopy: (value: string) => void
  item: ResourceMenuItemComponent
  separator: React.ComponentType
}

export function ResourceMenuItems({
  actions,
  copy,
  onCopy,
  item: Item,
  separator: Separator,
}: ResourceMenuItemsProps) {
  const standardActions = actions.filter((action) => !action.destructive)
  const destructiveActions = actions.filter((action) => action.destructive)
  const copyActions: ResourceMenuAction[] = []
  if (copy.id) {
    const id = copy.id
    copyActions.push({
      label: "Copy ID",
      icon: <Hash />,
      onSelect: () => onCopy(id),
      "data-testid": "resource-copy-id",
    })
  }
  if (copy.url) {
    const url = copy.url
    copyActions.push({
      label: "Copy URL",
      icon: <Link2 />,
      onSelect: () => onCopy(url),
      "data-testid": "resource-copy-url",
    })
  }
  const sections = [standardActions, copyActions, destructiveActions].filter(
    (section) => section.length > 0,
  )

  return sections.map((section, sectionIndex) => (
    <React.Fragment key={sectionIndex}>
      {sectionIndex > 0 && <Separator />}
      {section.map((action) => (
        <Item
          key={action.label}
          destructive={action.destructive}
          disabled={action.disabled || action.loading}
          onSelect={action.onSelect}
          data-testid={action["data-testid"]}
          className="gap-2 [&_svg]:size-4"
        >
          {action.loading ? (
            <Loader size="sm" className="size-4 text-current" />
          ) : (
            action.icon
          )}
          {action.label}
        </Item>
      ))}
    </React.Fragment>
  ))
}

export function normalizeResourceCopyTargets({
  id,
  url,
}: {
  id?: string | null
  url?: string | null
}): ResourceMenuCopyTargets {
  const canonicalPath = url?.split(/[?#]/, 1)[0]

  return {
    ...(id ? { id } : {}),
    ...(canonicalPath
      ? {
          url:
            typeof window === "undefined"
              ? canonicalPath
              : new URL(canonicalPath, window.location.origin).toString(),
        }
      : {}),
  }
}
